import { Box, Container, Typography, Divider, Paper } from '@mui/material';

function Privacy() {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
          IX-Study – Privacy Policy
        </Typography>

        <Typography paragraph sx={{ mb: 4, fontStyle: 'italic', color: 'text.secondary', textAlign: 'center' }}>
          Last Updated: April 18, 2025
        </Typography>

        <Typography paragraph sx={{ mb: 4 }}>
          Welcome to <strong>IX-Study</strong>! We respect your privacy and are committed to keeping your data safe and transparent. This policy explains what we collect, why we collect it, and how we use it.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          1. What Data We Collect
        </Typography>
        <Typography paragraph>
          We collect only what's needed to improve your experience. This can include:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph><strong>User Inputs</strong>: Any text or prompts you type into the app (used for AI features).</Typography>
          <Typography component="li" paragraph><strong>Usage Data</strong>: Info about how you use the app (which features, how often, etc.).</Typography>
          <Typography component="li" paragraph><strong>Device Info</strong>: Browser type, device model, and operating system for analytics and performance tuning.</Typography>
          <Typography component="li" paragraph><strong>Cookies & Tracking</strong>: We may use cookies or similar technologies to:
            <Typography component="ul" sx={{ pl: 4 }}>
              <Typography component="li" paragraph>Remember user preferences</Typography>
              <Typography component="li" paragraph>Analyze usage patterns</Typography>
              <Typography component="li" paragraph>Improve functionality</Typography>
            </Typography>
          </Typography>
        </Typography>
        <Typography paragraph sx={{ fontStyle: 'italic' }}>
          You can disable cookies in your browser settings if you prefer.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          2. How We Use Your Data
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph>Deliver and improve app features</Typography>
          <Typography component="li" paragraph>Personalize your learning experience</Typography>
          <Typography component="li" paragraph>Diagnose bugs and improve performance</Typography>
          <Typography component="li" paragraph>Enhance future updates and development</Typography>
        </Typography>
        <Typography paragraph>
          We <strong>don't sell your data</strong> or share it with advertisers.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          3. Data Security
        </Typography>
        <Typography paragraph>
          We use industry-standard security measures to protect your information. However, no method of transmission over the Internet or electronic storage is 100% secure. We encourage you to use strong passwords and keep your account information confidential.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          4. Children’s Privacy
        </Typography>
        <Typography paragraph>
          IX-Study is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal data, please contact us so we can remove it.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          5. Changes to This Policy
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy from time to time. Changes will be posted on this page. Your continued use of IX-Study means you accept the updated policy.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          6. Contact
        </Typography>
        <Typography paragraph>
          For privacy questions, requests, or concerns, contact us.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          7. Third-Party AI Services & Open Source Licenses
        </Typography>
        <Typography paragraph>
          IX-Study uses third-party AI, infrastructure, and open-source libraries to deliver its features. When you use AI-powered features, your data may be processed by or subject to:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph>
            <strong>Together AI</strong>: 
            <a href="https://www.together.ai/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Use</a> | 
            <a href="https://www.together.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (AI model inference and infrastructure)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Meta (LLaMA Models)</strong>: 
            <a href="https://ai.meta.com/llama/license/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Meta AI Terms</a> | 
            <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Meta Privacy Policy</a><br/>
            (Large language models for AI features)
          </Typography>
          <Typography component="li" paragraph>
            <strong>OpenRouter</strong>: 
            <a href="https://openrouter.ai/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</a> | 
            <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (AI API routing and access to multiple models)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Groq</strong>: 
            <a href="https://console.groq.com/docs/legal" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Legal Terms</a><br/>
            (By using IX-Study, you agree to Groq's terms: https://console.groq.com/docs/legal)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Apache License 2.0</strong>: 
            <a href="https://www.apache.org/licenses/LICENSE-2.0" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Full License Text</a><br/>
            (Some libraries and dependencies are licensed under Apache 2.0)
          </Typography>
          <Typography component="li" paragraph>
            <strong>Mistral AI</strong>: 
            <a href="https://mistral.ai/terms-of-use/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Use</a> | 
            <a href="https://mistral.ai/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a><br/>
            (Large language models for AI features)
          </Typography>
          <Typography component="li" paragraph>
            <strong>MIT License</strong>: 
            <a href="https://opensource.org/licenses/MIT" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Full License Text</a><br/>
            (Some libraries and dependencies are licensed under MIT)
          </Typography>
        </Typography>
        <Typography paragraph>
          Please review their policies for details on how your data is handled. By using IX-Study, you agree to these terms and policies.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          8. Cookies and Analytics
        </Typography>
        <Typography paragraph>
          We may use:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <Typography component="li" paragraph><strong>Cookies</strong> to save your preferences and session data</Typography>
          <Typography component="li" paragraph><strong>Analytics tools</strong> to understand user behavior (anonymized)</Typography>
        </Typography>
        <Typography paragraph>
          You're free to clear or block cookies anytime in your browser.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          9. Children's Privacy
        </Typography>
        <Typography paragraph>
          IX-Study is made for students. If you're under 13, use this app <strong>only with a parent/guardian's permission</strong>. We don't knowingly collect personal info from children.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          10. Data Security
        </Typography>
        <Typography paragraph>
          We use modern tools and best practices to secure your data. But no app is 100% unhackable, so stay safe on your end too.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          11. Changes to This Policy
        </Typography>
        <Typography paragraph>
          If we update this policy, we'll let you know in-app or on the site.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          12. Contact Us
        </Typography>
        <Typography paragraph>
          Questions or feedback?<br />
          📧 <a href="mailto:TIXOhelp@protonmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>TIXOhelp@protonmail.com</a>
        </Typography>
      </Paper>
    </Container>
  );
}

export default Privacy;